import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw, Loader2 } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { AdminLogin } from "../components/admin/AdminLogin";
import { LeadCard, STATUSES } from "../components/admin/LeadCard";
import { api, getToken, setToken, errorText } from "../components/admin/api";

const FILTERS = [{ id: "todas", label: "Todas" }, ...STATUSES];

export default function AdminPage() {
  const [user, setUser] = useState(getToken() ? null : false);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const logout = useCallback(() => { setToken(null); setUser(false); setLeads([]); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) logout();
      else toast.error(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (user !== null) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => logout());
  }, [user, logout]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const changeStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusyId(null);
    }
  };

  if (user === false) return <AdminLogin onLogin={setUser} />;
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-arena"><Loader2 className="h-8 w-8 animate-spin text-mar" /></div>;

  const count = (id) => (id === "todas" ? leads.length : leads.filter((l) => l.status === id).length);
  const shown = filter === "todas" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-arena">
      <header className="sticky top-0 z-40 border-b border-mar/10 bg-arena/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="/" data-testid="admin-home-link"><Logo /></a>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-mar/20 px-4 py-2 text-sm font-bold text-mar hover:bg-white">
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Salir</span>
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-barro">Panel</p>
            <h1 className="mt-2 font-display text-4xl font-extrabold text-mar sm:text-5xl">Solicitudes de viaje</h1>
          </div>
          <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-2 rounded-full bg-mar px-5 py-3 text-sm font-bold text-white">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`admin-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === f.id ? "bg-mar text-white" : "bg-white text-mar hover:bg-mar/10"}`}>
              {f.label} <span className="ml-1 opacity-70">{count(f.id)}</span>
            </button>
          ))}
        </div>
        <div className="mt-6 grid gap-4">
          {shown.map((l) => <LeadCard key={l.id} lead={l} onStatus={changeStatus} busy={busyId === l.id} />)}
          {!loading && shown.length === 0 && (
            <p data-testid="admin-empty" className="rounded-3xl bg-white p-10 text-center text-tinta/70">No hay solicitudes en esta categoría.</p>
          )}
        </div>
      </main>
    </div>
  );
}
